import React, { useEffect, useRef, useState } from 'react'
import classNames from 'classnames'
import get from 'lodash/get'
import { useTranslation } from 'react-i18next'

import Chevron from '@components/icons/chevron'
import Markdown from '@components/markdown'
import MaterialSetBackground from '@backgrounds/material_set_background'
import MaterialSetDesktopBackground from '@backgrounds/material_set_desktop_background'
import MaterialSetTypeBackground from '@backgrounds/material_set_type_background'
import MaterialSetTypeDesktopBackground from '@backgrounds/material_set_type_desktop_background'
import { getMaterialTags } from '@util/materials'
import styles from '@components/blocks/components/material_set.module.scss'
import ImageAsset from '@components/image_asset'

const MaterialSet = ({ fields, color, contentAreasLabel, contentGuidelinesAgreement }) => {
  const { slug, name, type, image, description, instructions, languageCount, materialCategories } =
    fields

  const { t } = useTranslation()
  const [downloadsExpanded, setDownloadsExpanded] = useState(false)
  const containerEl = useRef(null)
  const contentAreaTagNames = getMaterialTags(materialCategories)
    .filter(tag => tag.fields.category === 'Content Area')
    .map(tag => tag.fields.title)
    .sort((a, b) => a.localeCompare(b))

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const params = new URLSearchParams(window.location.search)
      const materialSetParam = params.get('materialSet')

      if (materialSetParam && materialSetParam === slug && containerEl.current !== null) {
        setTimeout(() => containerEl.current.scrollIntoView(), 0)
      }
    }
  }, [slug])

  const headerColorClass = styles[`material_set__header--${color}`]

  const categories = materialCategories || []

  const expandButtonText = downloadsExpanded
    ? t('closeDownloads')
    : t('expandDownloads', { count: languageCount })

  const toggleDownloads = () => {
    setDownloadsExpanded(!downloadsExpanded)
  }

  const rootClasses = [styles.material_set]
  if (downloadsExpanded) {
    rootClasses.push(styles['material_set--open_downloads'])
  }

  return (
    <div className={classNames(rootClasses)}>
      <div ref={containerEl} className={classNames(styles.material_set__header, headerColorClass)}>
        <div className={styles.material_set__header_background}>
          <MaterialSetBackground />
        </div>
        <div className={styles.material_set__header_desktop_background}>
          <MaterialSetDesktopBackground />
        </div>
        <div className={styles.material_set__type_background}>
          <MaterialSetTypeBackground />
        </div>
        <div className={styles.material_set__type_desktop_background}>
          <MaterialSetTypeDesktopBackground />
        </div>
        <div className={styles.material_set__type}>{type}</div>
        <div className={styles.material_set__name}>{name}</div>
        <div className={styles.material_set__image}>
          <ImageAsset asset={image} height="275" aria-hidden />
        </div>
      </div>
      <div className={styles.material_set__content}>
        <Markdown className={styles.material_set__description}>{description}</Markdown>
        <div className={styles.material_set__description_bottom}>
          {contentAreaTagNames.length > 0 && (
            <div>
              <div className={styles.material_set__content_area_label}>{contentAreasLabel}</div>
              <div className={styles.material_set__content_area_list}>
                {contentAreaTagNames.join(', ')}
              </div>
            </div>
          )}
          <Markdown className={styles.material_set__instructions}>{instructions}</Markdown>
          <div className={styles.material_set__expand_downloads}>
            <button className={styles.material_set__expand_button} onClick={toggleDownloads}>
              {expandButtonText}
              <Chevron className={styles.material_set__expand_chevron} />
            </button>
          </div>
        </div>
      </div>
      {downloadsExpanded && (
        <div className={styles.material_set__downloads}>
          {categories.map(category => (
            <MaterialCategory key={category.id} fields={category.fields} materialSetName={name} />
          ))}
          <Markdown className={styles.material_set__agreement}>
            {contentGuidelinesAgreement}
          </Markdown>
        </div>
      )}
    </div>
  )
}

const MaterialCategory = ({ fields: { displayText, materials }, materialSetName }) => {
  const categoryColumns = Math.min(Math.ceil(materials.length / 4), 3)

  return (
    <div className={styles.material_set__category}>
      <div className={styles.material_set__category_name}>{displayText}</div>
      <ul
        className={classNames(
          styles.material_set__category_list,
          styles[`material_set__category_list--columns_${categoryColumns}`]
        )}
      >
        {materials.map(material => (
          <li key={material.id}>
            <Material
              fields={material.fields}
              materialSetName={materialSetName}
              categoryDisplayText={displayText}
            />
          </li>
        ))}
      </ul>
    </div>
  )
}

const Material = ({ fields: { displayText, file }, materialSetName, categoryDisplayText }) => {
  const fileUrl = get(file, 'fields.file.url')

  return (
    <a
      className={styles.material_set__category_download}
      href={fileUrl}
      target="_blank"
      rel="noopener noreferrer"
    >
      {displayText}
    </a>
  )
}

export default MaterialSet
